import type { Sprite } from './atlas';

export function darkenHex(hex: string, amount: number): string {
  const long =
    hex.length === 4
      ? `#${hex.charAt(1)}${hex.charAt(1)}${hex.charAt(2)}${hex.charAt(2)}${hex.charAt(3)}${hex.charAt(3)}`
      : hex;
  const r = parseInt(long.slice(1, 3), 16);
  const g = parseInt(long.slice(3, 5), 16);
  const b = parseInt(long.slice(5, 7), 16);
  const scale = 1 - amount;
  const toHex = (channel: number): string =>
    Math.round(channel * scale)
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/** `color` blend repaints the hull from its grey luminance; `destination-in` clips the fill back to the hull's alpha. `darken` exists because `color` ignores the fillStyle's lightness, so a darker hex cannot darken anything; it lays black over the finished pixels with `source-atop`, which is bounded by what has already been drawn — most of a base's art lives in the detail layer, and re-clipping to the hull would erase it. */
export function tintSprite(
  hull: Sprite,
  detail: Sprite,
  color: string,
  darken = 0,
): HTMLCanvasElement | null {
  const canvas = document.createElement('canvas');
  canvas.width = hull.sw;
  canvas.height = hull.sh;
  const ctx = canvas.getContext('2d');
  if (ctx === null) return null;

  const draw = (sprite: Sprite): void => {
    ctx.drawImage(sprite.image, sprite.sx, sprite.sy, sprite.sw, sprite.sh, 0, 0, hull.sw, hull.sh);
  };

  draw(hull);
  ctx.globalCompositeOperation = 'color';
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, hull.sw, hull.sh);
  ctx.globalCompositeOperation = 'destination-in';
  draw(hull);
  ctx.globalCompositeOperation = 'source-over';
  draw(detail);

  if (darken > 0) {
    ctx.globalCompositeOperation = 'source-atop';
    ctx.fillStyle = `rgba(0, 0, 0, ${String(darken)})`;
    ctx.fillRect(0, 0, hull.sw, hull.sh);
    ctx.globalCompositeOperation = 'source-over';
  }

  return canvas;
}

/** Hue-shifts only the pure-lime key pixels the art is drawn with; the narrow pixel test leaves everything else in its own colours. Shifts are HSL degrees/percent. */
export function limeHuedSprite(
  sprite: Sprite,
  hueShift: number,
  saturationShift: number,
  luminanceShift: number,
): HTMLCanvasElement | null {
  const canvas = document.createElement('canvas');
  canvas.width = sprite.sw;
  canvas.height = sprite.sh;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (ctx === null) return null;
  ctx.drawImage(
    sprite.image,
    sprite.sx,
    sprite.sy,
    sprite.sw,
    sprite.sh,
    0,
    0,
    sprite.sw,
    sprite.sh,
  );

  let data: ImageData;
  try {
    data = ctx.getImageData(0, 0, canvas.width, canvas.height);
  } catch {
    /** Tainted canvas (cross-origin atlas): unhued art is wrong for the team but still the right shape, which beats drawing nothing. */
    return canvas;
  }

  const pixels = data.data;
  for (let p = 0; p + 3 < pixels.length; p += 4) {
    const red = pixels[p] ?? 0;
    const green = pixels[p + 1] ?? 0;
    const blue = pixels[p + 2] ?? 0;
    if (green < 250 || red > 10 || blue > 10) continue;
    const [h, s, l] = rgbToHsl(red, green, blue);
    const [r, g, b] = hslToRgb(h + hueShift, s + saturationShift, l + luminanceShift);
    pixels[p] = r;
    pixels[p + 1] = g;
    pixels[p + 2] = b;
  }
  ctx.putImageData(data, 0, 0);
  return canvas;
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  const rf = r / 255;
  const gf = g / 255;
  const bf = b / 255;
  const max = Math.max(rf, gf, bf);
  const min = Math.min(rf, gf, bf);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l * 100];

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === rf) h = (gf - bf) / d + (gf < bf ? 6 : 0);
  else if (max === gf) h = (bf - rf) / d + 2;
  else h = (rf - gf) / d + 4;
  return [(h / 6) * 360, s * 100, l * 100];
}

function hslToRgb(hDegrees: number, sPercent: number, lPercent: number): [number, number, number] {
  const h = hDegrees / 360;
  const s = Math.max(0, Math.min(1, sPercent / 100));
  const l = Math.max(0, Math.min(1, lPercent / 100));
  if (s === 0) {
    const v = Math.round(l * 255);
    return [v, v, v];
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [
    Math.round(hue2rgb(p, q, h + 1 / 3) * 255),
    Math.round(hue2rgb(p, q, h) * 255),
    Math.round(hue2rgb(p, q, h - 1 / 3) * 255),
  ];
}

function hue2rgb(p: number, q: number, t: number): number {
  let v = t % 1;
  if (v < 0) v += 1;
  if (v < 1 / 6) return p + (q - p) * 6 * v;
  if (v < 1 / 2) return q;
  if (v < 2 / 3) return p + (q - p) * (2 / 3 - v) * 6;
  return p;
}
